import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, ChevronDown, ChevronUp, Clock, Loader2, Plus, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import { schedulesApi, queryKeys, ScheduleRule, ScheduleRuleInput, ScheduleRulesImpact } from '@/lib/api';
import { fmtInTz } from '@/lib/utils';

/**
 * Schedule Rules — "when the game title contains X, the game lasts N minutes".
 *
 * Where a schedule source sends no game length, CSM assumes the site's default
 * length (2 hours unless the site sets its own, at the top of this panel). A rule
 * gives the real length so CSM stops warning about overlaps that do not exist
 * on the ice. A rule normally only fills in where the source is silent; ticking
 * Override makes it beat the source's own length too.
 *
 * Edits are a draft of the WHOLE list, previewed against the upcoming games
 * before anything is saved — a bad pattern silently reshaping a season is the
 * failure mode this guards against, and precedence is positional, so only the
 * whole list can say what a change really does.
 */

type DraftRule = ScheduleRuleInput & { key: string };

const inputCls =
  'rounded-md border border-gray-300 bg-white px-2 py-1 text-sm text-gray-900 focus:outline-none focus:ring-2 ' +
  'focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100';

function fmtLength(min: number): string {
  const h = Math.floor(min / 60), m = min % 60;
  return h === 0 ? `${m} min` : m === 0 ? `${h} h` : `${h} h ${m} min`;
}

const MATCH_FIELDS: Array<{ value: ScheduleRuleInput['matchField']; label: string; placeholder: string; hint: string }> = [
  { value: 'eventName', label: 'Event name', placeholder: 'e.g. Doubles', hint: 'contains' },
  { value: 'teams', label: 'Teams', placeholder: 'e.g. Juniors', hint: 'either team contains' },
  { value: 'sheet', label: 'Sheet', placeholder: 'e.g. A  or  A, C', hint: 'is exactly' },
];

const asMatchField = (v: string | undefined): ScheduleRuleInput['matchField'] =>
  v === 'teams' || v === 'sheet' ? v : 'eventName';

const toDraft = (r: ScheduleRule): DraftRule => ({
  key: `id:${r.id}`, id: r.id, matchValue: r.matchValue, matchField: asMatchField(r.matchField),
  action: r.action === 'skip' ? 'skip' : 'duration',
  // A skip rule stores 0; give it a sensible length if someone switches it back.
  durationMinutes: r.action === 'skip' ? 90 : r.durationMinutes,
  overridesSource: r.overridesSource, isActive: r.isActive,
});

// What the server stores: a skip rule has no length and overrides nothing.
const strip = (d: DraftRule): ScheduleRuleInput => ({
  ...(d.id ? { id: d.id } : {}), matchValue: d.matchValue.trim(), matchField: d.matchField, action: d.action,
  durationMinutes: d.action === 'skip' ? 0 : d.durationMinutes,
  overridesSource: d.action === 'skip' ? false : d.overridesSource, isActive: d.isActive,
});

function draftError(d: DraftRule): string | null {
  if (!d.matchValue.trim()) return 'Enter the text to match';
  if (d.action === 'skip') return null;
  if (!Number.isInteger(d.durationMinutes) || d.durationMinutes < 10 || d.durationMinutes > 600) {
    return 'Length must be 10–600 minutes';
  }
  return null;
}

export default function ScheduleRulesPanel({ facilityId, facilityTimezone }: {
  facilityId: string;
  facilityTimezone?: string;
}) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<DraftRule[] | null>(null);
  const [impact, setImpact] = useState<ScheduleRulesImpact | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedNote, setSavedNote] = useState<string | null>(null);
  // The site's default length, as typed. null = not being edited.
  const [defaultDraft, setDefaultDraft] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.schedules.rules(facilityId),
    queryFn: () => schedulesApi.listScheduleRules(facilityId),
  });

  const saved = useMemo(() => (data?.rules ?? []).map(toDraft), [data]);
  const rows = draft ?? saved;
  const rulesDirty = draft !== null && JSON.stringify(draft.map(strip)) !== JSON.stringify(saved.map(strip));
  const errors = rows.map(draftError);

  // Default length: blank = back to the built-in fallback.
  const fallback = data?.fallbackMinutes ?? 120;
  const savedDefault = data?.defaultMinutes ?? null;
  const defaultText = defaultDraft ?? (savedDefault === null ? '' : String(savedDefault));
  const parsedDefault: number | null = defaultText.trim() === '' ? null : Number(defaultText);
  const defaultError = parsedDefault !== null && (!Number.isInteger(parsedDefault) || parsedDefault < 30 || parsedDefault > 360)
    ? 'Default length must be 30–360 minutes, or blank for 2 hours'
    : null;
  const defaultDirty = defaultDraft !== null && parsedDefault !== savedDefault;
  const effectiveDefault = (defaultError ? savedDefault : parsedDefault) ?? fallback;

  const dirty = rulesDirty || defaultDirty;
  const valid = errors.every(e => e === null) && defaultError === null;

  // Live impact of the draft, debounced so typing a pattern does not fire a
  // request per keystroke.
  const previewMutation = useMutation({
    mutationFn: (v: { rules: ScheduleRuleInput[]; defaultMinutes: number | null }) =>
      schedulesApi.previewScheduleRules({ facilityId, ...v }),
    onSuccess: setImpact,
  });
  const draftSignature = JSON.stringify([rows.map(strip), parsedDefault]);
  useEffect(() => {
    if (!dirty || !valid) { setImpact(null); return undefined; }
    const t = setTimeout(() => previewMutation.mutate({ rules: rows.map(strip), defaultMinutes: parsedDefault }), 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftSignature, dirty, valid]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (defaultDirty) await schedulesApi.setDefaultGameLength({ facilityId, defaultMinutes: parsedDefault });
      if (!rulesDirty) return;
      const next = rows;
      const savedById = new Map((data?.rules ?? []).map(r => [r.id, r]));
      const keptIds = new Set(next.filter(d => d.id).map(d => d.id!));
      for (const r of data?.rules ?? []) {
        if (!keptIds.has(r.id)) await schedulesApi.deleteScheduleRule(r.id);
      }
      const finalIds: number[] = [];
      for (const d of next) {
        const body = strip(d);
        if (d.id) {
          const before = savedById.get(d.id)!;
          if (before.matchValue !== body.matchValue || before.action !== body.action ||
              asMatchField(before.matchField) !== body.matchField ||
              before.durationMinutes !== body.durationMinutes ||
              before.overridesSource !== body.overridesSource || before.isActive !== body.isActive) {
            await schedulesApi.updateScheduleRule(d.id, {
              matchValue: body.matchValue, matchField: body.matchField, action: body.action,
              durationMinutes: body.durationMinutes,
              overridesSource: body.overridesSource, isActive: body.isActive,
            });
          }
          finalIds.push(d.id);
        } else {
          const { rule } = await schedulesApi.createScheduleRule({ ...body, facilityId });
          finalIds.push(rule.id);
        }
      }
      if (finalIds.length > 0) await schedulesApi.reorderScheduleRules({ facilityId, ids: finalIds });
    },
    onSuccess: () => {
      setSaveError(null);
      setSavedNote(impact && impact.changedCount > 0
        ? `Saved. CSM is re-syncing your schedule now — ${impact.changedCount} upcoming game${impact.changedCount === 1 ? '' : 's'} will update within a minute or two.`
        : 'Saved. CSM is re-syncing your schedule now.');
      setDraft(null);
      setDefaultDraft(null);
      setImpact(null);
      queryClient.invalidateQueries({ queryKey: queryKeys.schedules.rules(facilityId) });
      // The re-sync runs in the background on the server; refresh the list once
      // it has had time to land.
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: queryKeys.schedules.all });
      }, 15_000);
    },
    onError: (err: unknown) => {
      setSaveError(err instanceof Error ? err.message : 'Could not save the rules');
      queryClient.invalidateQueries({ queryKey: queryKeys.schedules.rules(facilityId) });
    },
  });

  const edit = (key: string, patch: Partial<DraftRule>) => {
    setSavedNote(null);
    setDraft(rows.map(r => (r.key === key ? { ...r, ...patch } : r)));
  };
  const move = (idx: number, dir: -1 | 1) => {
    const j = idx + dir;
    if (j < 0 || j >= rows.length) return;
    const next = [...rows];
    [next[idx], next[j]] = [next[j]!, next[idx]!];
    setSavedNote(null);
    setDraft(next);
  };
  const remove = (key: string) => { setSavedNote(null); setDraft(rows.filter(r => r.key !== key)); };
  const add = () => {
    setSavedNote(null);
    setDraft([...rows, {
      key: `new:${Date.now()}`, matchValue: '', matchField: 'eventName', action: 'duration', durationMinutes: 90,
      overridesSource: !!data?.overrideOnly, isActive: true,
    }]);
  };

  const summary = data?.summary;
  const ruleCount = data?.rules.length ?? 0;

  return (
    <div className="rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900">
      <button
        onClick={() => setOpen(v => !v)}
        className="flex w-full items-center justify-between px-4 py-3 text-left"
      >
        <span className="flex flex-wrap items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
          <Clock className="h-4 w-4 text-blue-500" />
          Schedule Rules
          <span className="font-normal text-gray-500 dark:text-gray-400">
            {isLoading ? '' : ruleCount === 0
              ? '— set how long your games really last'
              : `— ${ruleCount} rule${ruleCount === 1 ? '' : 's'}`}
            {summary && summary.onDefault > 0
              ? ` · ${summary.onDefault} upcoming game${summary.onDefault === 1 ? '' : 's'} on the default length (${fmtLength(savedDefault ?? fallback)})`
              : ''}
            {summary?.skipped
              ? ` · ${summary.skipped} upcoming game${summary.skipped === 1 ? '' : 's'} skipped by a rule`
              : ''}
          </span>
        </span>
        {open
          ? <ChevronUp className="h-4 w-4 shrink-0 text-gray-400" />
          : <ChevronDown className="h-4 w-4 shrink-0 text-gray-400" />}
      </button>

      {open && (
        <div className="space-y-3 px-4 pb-4 text-sm">
          <p className="text-xs text-gray-600 dark:text-gray-400">
            When your schedule source doesn’t say how long a game lasts, CSM uses the default length below. That can make two
            games on a sheet look like they overlap when they don’t. A rule tells CSM the real length: when the
            game matches, it lasts that long. A rule can match the{' '}
            <span className="font-medium text-gray-800 dark:text-gray-200">event name</span>, either{' '}
            <span className="font-medium text-gray-800 dark:text-gray-200">team</span>, or the{' '}
            <span className="font-medium text-gray-800 dark:text-gray-200">sheet</span> (exact, e.g. “A” or “A, C”).
            Rules are checked top to bottom and
            the <span className="font-medium text-gray-800 dark:text-gray-200">first match wins</span>, so put the
            more specific ones first. Normally a rule only applies where your source gives no length —
            tick <span className="font-medium text-gray-800 dark:text-gray-200">Override</span> to use it even when
            your source does.
          </p>
          <p className="text-xs text-gray-600 dark:text-gray-400">
            Set a rule’s action to <span className="font-medium text-gray-800 dark:text-gray-200">Skip</span> to stop
            matching games from streaming (practices, juniors, private bookings, a sheet you don’t broadcast). Skipped games
            still appear in your schedule, marked <span className="italic">Skipped by rule</span>, and a Skip rule
            wins over any length rule wherever it sits in the list. To stream one skipped game anyway, press
            <span className="font-medium text-gray-800 dark:text-gray-200"> Resume</span> on it.
          </p>

          {/* The site's default: the last resort, used only where neither the
              source nor a rule gives a game a length. */}
          {!isLoading && !data?.overrideOnly && (
            <div className="flex flex-wrap items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-700 dark:bg-gray-800/60">
              <label htmlFor="default-game-length" className="text-sm font-medium text-gray-900 dark:text-gray-100">
                Default game length
              </label>
              <input
                id="default-game-length"
                type="number" min={30} max={360} step={5}
                value={defaultText}
                placeholder={String(fallback)}
                onChange={e => { setSavedNote(null); setDefaultDraft(e.target.value); }}
                className={`${inputCls} w-20`}
              />
              <span className="text-xs text-gray-600 dark:text-gray-400">
                minutes{effectiveDefault >= 60 ? ` (${fmtLength(effectiveDefault)})` : ''} — used only when your source gives a game
                no length and no rule below matches it. Leave blank for {fmtLength(fallback)}.
              </span>
              {defaultError && <span className="w-full text-xs text-red-600 dark:text-red-400">{defaultError}</span>}
            </div>
          )}

          {data?.overrideOnly && (
            <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-900 dark:border-blue-900 dark:bg-blue-950/40 dark:text-blue-200">
              Your Google Sheet gives every game its own end time, so here a rule only changes anything when
              <span className="font-medium"> Override</span> is ticked — it then replaces the sheet’s end time in CSM
              (your sheet itself is not changed). The default game length doesn’t apply to sheet schedules.
            </div>
          )}

          {data && !data.ruleCapable && (
            <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
              This facility’s schedule source sets its own end times, so rules have no effect here. They apply
              to CurlingClubManager, LeagueApps and CurlOps schedules.
            </div>
          )}

          {isLoading ? (
            <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin text-gray-400" /></div>
          ) : (
            <div className="overflow-x-auto rounded-md border border-gray-200 dark:border-gray-700">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-xs text-gray-600 dark:bg-gray-800 dark:text-gray-400">
                  <tr>
                    <th className="w-16 px-2 py-2 text-left font-medium">Order</th>
                    <th className="px-2 py-2 text-left font-medium">When</th>
                    <th className="px-2 py-2 text-left font-medium">Action</th>
                    <th className="px-2 py-2 text-left font-medium">Length (minutes)</th>
                    <th className="px-2 py-2 text-center font-medium" title="Use this length even when your source sends one">Override</th>
                    <th className="px-2 py-2 text-center font-medium">Active</th>
                    <th className="w-10 px-2 py-2" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-3 py-4 text-center text-xs text-gray-500 dark:text-gray-400">
                        No rules yet — every game without a length from your source uses the default length ({fmtLength(effectiveDefault)}).
                      </td>
                    </tr>
                  ) : rows.map((r, i) => (
                    <tr key={r.key} className="text-gray-900 dark:text-gray-100">
                      <td className="px-2 py-1.5">
                        <div className="flex items-center gap-0.5 text-gray-500 dark:text-gray-400">
                          <span className="w-4 text-xs">{i + 1}</span>
                          <button title="Move up" disabled={i === 0} onClick={() => move(i, -1)}
                            className="rounded p-0.5 hover:bg-gray-100 disabled:opacity-30 dark:hover:bg-gray-800">
                            <ArrowUp className="h-3.5 w-3.5" />
                          </button>
                          <button title="Move down" disabled={i === rows.length - 1} onClick={() => move(i, 1)}
                            className="rounded p-0.5 hover:bg-gray-100 disabled:opacity-30 dark:hover:bg-gray-800">
                            <ArrowDown className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </td>
                      <td className="px-2 py-1.5">
                        {(() => {
                          const mf = MATCH_FIELDS.find(f => f.value === r.matchField) ?? MATCH_FIELDS[0]!;
                          return (
                            <div className="flex flex-wrap items-center gap-1.5">
                              <select
                                value={r.matchField}
                                onChange={e => edit(r.key, { matchField: asMatchField(e.target.value) })}
                                aria-label="What to match"
                                className={inputCls}
                              >
                                {MATCH_FIELDS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                              </select>
                              <span className="text-xs text-gray-500 dark:text-gray-400">{mf.hint}</span>
                              <input
                                value={r.matchValue}
                                onChange={e => edit(r.key, { matchValue: e.target.value })}
                                placeholder={mf.placeholder}
                                maxLength={100}
                                className={`${inputCls} min-w-[9rem] flex-1`}
                              />
                            </div>
                          );
                        })()}
                        {errors[i] && <div className="mt-0.5 text-xs text-red-600 dark:text-red-400">{errors[i]}</div>}
                      </td>
                      <td className="px-2 py-1.5">
                        <select
                          value={r.action}
                          onChange={e => edit(r.key, { action: e.target.value === 'skip' ? 'skip' : 'duration' })}
                          aria-label="Rule action"
                          className={inputCls}
                        >
                          <option value="duration">Set length</option>
                          <option value="skip">Skip — don’t stream</option>
                        </select>
                      </td>
                      <td className="px-2 py-1.5">
                        {r.action === 'skip' ? (
                          <span className="text-xs text-gray-500 dark:text-gray-400">—</span>
                        ) : (
                        <div className="flex items-center gap-2">
                          <input
                            type="number" min={10} max={600} step={5}
                            value={Number.isFinite(r.durationMinutes) ? r.durationMinutes : ''}
                            onChange={e => edit(r.key, { durationMinutes: e.target.value === '' ? NaN : Number(e.target.value) })}
                            className={`${inputCls} w-20`}
                          />
                          {Number.isFinite(r.durationMinutes) && r.durationMinutes >= 60 && (
                            <span className="whitespace-nowrap text-xs text-gray-500 dark:text-gray-400">{fmtLength(r.durationMinutes)}</span>
                          )}
                        </div>
                        )}
                      </td>
                      <td className="px-2 py-1.5 text-center">
                        {r.action === 'skip' ? (
                          <span className="text-xs text-gray-500 dark:text-gray-400">—</span>
                        ) : (<>
                        <input type="checkbox" checked={r.overridesSource}
                          onChange={e => edit(r.key, { overridesSource: e.target.checked })}
                          title="Use this length even when your source sends one"
                          className="h-4 w-4 accent-blue-600" />
                        {data?.overrideOnly && !r.overridesSource && (
                          <div className="mt-0.5 text-[10px] font-medium text-amber-700 dark:text-amber-400">no effect here</div>
                        )}
                        </>)}
                      </td>
                      <td className="px-2 py-1.5 text-center">
                        <input type="checkbox" checked={r.isActive}
                          onChange={e => edit(r.key, { isActive: e.target.checked })}
                          className="h-4 w-4 accent-blue-600" />
                      </td>
                      <td className="px-2 py-1.5 text-right">
                        <button title="Remove rule" onClick={() => remove(r.key)}
                          className="rounded p-1 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={add}>
              <Plus className="mr-1 h-4 w-4" /> Add rule
            </Button>
            {dirty && (
              <>
                <Button size="sm" disabled={!valid || saveMutation.isPending} onClick={() => saveMutation.mutate()}>
                  {saveMutation.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
                  Save changes
                </Button>
                <Button variant="outline" size="sm" disabled={saveMutation.isPending}
                  onClick={() => { setDraft(null); setDefaultDraft(null); setImpact(null); setSaveError(null); }}>
                  Discard
                </Button>
              </>
            )}
            {savedNote && <span className="text-xs text-green-700 dark:text-green-400">{savedNote}</span>}
            {saveError && <span className="text-xs text-red-600 dark:text-red-400">{saveError}</span>}
          </div>

          {/* Impact preview: what the draft would do to games already imported. */}
          {dirty && valid && (
            <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-900 dark:border-blue-900 dark:bg-blue-950/40 dark:text-blue-200">
              {previewMutation.isPending && !impact ? (
                <span className="flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Checking upcoming games…</span>
              ) : impact ? (
                <>
                  <div className="font-medium text-blue-950 dark:text-blue-100">
                    {impact.changedCount === 0
                      ? `These changes don't alter any of the ${impact.upcomingGames} upcoming games currently imported.`
                      : `These changes alter ${impact.changedCount} of the ${impact.upcomingGames} upcoming games currently imported.`}
                  </div>
                  {impact.changed.length > 0 && (
                    <ul className="ml-4 mt-1 list-disc">
                      {impact.changed.map(g => (
                        <li key={g.id}>
                          <span className="font-medium">{g.eventName}</span> — sheet {g.sheetId},{' '}
                          {fmtInTz(g.scheduledStart, facilityTimezone)}:{' '}
                          {g.change === 'skip' ? <span className="font-medium">won’t stream (skipped)</span>
                            : g.change === 'unskip' ? <span className="font-medium">will stream again</span>
                            : <>{fmtLength(g.currentMinutes)} → {fmtLength(g.newMinutes)}</>}
                        </li>
                      ))}
                      {impact.changedCount > impact.changed.length && (
                        <li>…and {impact.changedCount - impact.changed.length} more</li>
                      )}
                    </ul>
                  )}
                  <div className="mt-1 text-blue-800 dark:text-blue-300">
                    After this: {impact.onSource} from your source, {impact.onRule} from a rule, {impact.onDefault} on the default length
                    {impact.skipped ? `, ${impact.skipped} skipped` : ''}.
                    {impact.notYetSynced > 0 && ` ${impact.notYetSynced} not checked yet — they haven't synced since rules were added.`}
                    {' '}Games further ahead are imported as their dates come closer and get the same rules.
                  </div>
                </>
              ) : null}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
